<template>
  <div class="day row" :class="getDayClass">
    <div class="datetime" :aria-label="getDayAriaLabel">
      <span class="date-text">{{
        formatDateAsDayWithSuffixShortMonth(rota.startDate, dayIndex)
      }}</span>
      <span v-if="dayIsToday"> (today)</span>
      <template v-if="currentShiftsForUserId.length > 0">
        <span class="shift-time">
          {{ formatTimeHoursMins(currentShiftsForUserId[0].startDate) }} -
          {{ formatTimeHoursMins(currentShiftsForUserId[0].endDate) }}
        </span>
      </template>
      <span v-else class="shift-time no-current">No shifts</span>
    </div>
    <div class="col-md-8">
      <div v-if="oncall === false || oncall.length > 0" class="oncall">
        On-call:
        <span v-if="oncall === false">
          <font-awesome-icon icon="spinner" spin pulse />
        </span>
        <template v-else>
          <span
            v-for="(oncallEntry, oncallIndex) in getOncallUsersForDayIndex"
            :key="oncallIndex"
            data-dd-privacy="mask"
          >
            <user-display-resolver
              :user-id="oncallEntry.userId"
              :display-name="oncallEntry.displayName"
            />
            {{ oncallEntry.phone }}
          </span>
        </template>
      </div>
      <p v-if="currentShiftsForUserId.length == 0" class="no-current">
        You have no shifts assigned
      </p>
      <div>
        <div
          class="shifts"
          :class="{
            today: rotaDateIsToday(rota.startDate, dayIndex),
          }"
        >
          <template v-for="(shift, index) in shiftsForUserId" :key="index">
            <div
              :id="`shift-${shift.id}`"
              class="shift"
              :class="{
                [`type-${getShiftAsignmentType(shift)}`]: 1,
              }"
            >
              <rota-shift-with-cover-request
                :rota-type="rota.type"
                :rota-status="rota.status"
                :rota-id="rota.id"
                :shift="shift"
                :activities="activities"
                @rota-shift-self-assign="$emit('rota-shift-self-assign', shift)"
              />
            </div>
          </template>
          <!-- shift cover requests: available shifts -->
          <template v-if="dayIsCurrent && !authStore.isAgencyUser">
            <div
              v-for="(shift, index) in shiftsNotForUserId"
              :id="`shift-${shift.id}`"
              :key="index"
              class="shift"
            >
              <rota-shift-with-cover-request
                :rota-type="rota.type"
                :rota-status="rota.status"
                :rota-id="rota.id"
                :shift="shift"
                :activities="activities"
                @rota-shift-self-assign="$emit('rota-shift-self-assign', shift)"
              />
            </div>
          </template>
        </div>
      </div>
    </div>
    <div class="col-md-4 activities">
      <template v-if="activities !== false">
        <div
          v-if="activitiesForDayIndex.length > 0"
          class="unassigned-activities"
        >
          <h3>Other Activities</h3>
          <template v-if="locationClients.length > 0">
            <rota-activity-card
              v-for="(activity, index) in activitiesForDayIndex"
              :id="activity.id"
              :key="index"
              :activity="activity"
              :show-shifts="false"
              :show-end-time="false"
              class="mb-1"
            />
          </template>
          <template v-else>
            <div class="placeholder-glow">
              <Placeholder />
            </div>
          </template>
        </div>
      </template>
      <template v-else>
        <div class="placeholder-glow">
          <Placeholder />
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { computed } from "vue";
import filter from "lodash/filter";
import find from "lodash/find";
import findLast from "lodash/findLast";
import get from "lodash/get";
import {
  addDays,
  isSameDay,
  isToday,
  isFuture,
  setMilliseconds,
} from "date-fns";
import { useAuthStore } from "@switchsystems/coactive-vue-lib";
import RotaActivityCard from "@/components/activity/RotaActivityCard.vue";
import Placeholder from "@/components/ui/Placeholder.vue";
import UserDisplayResolver from "@/components/UserDisplayResolver.vue";
import RotaShiftWithCoverRequest from "@/components/rota/shift/RotaShiftWithCoverRequest.vue";

import { getActivitiesForRotaDay } from "@//lib/rota/activity-functions";
import {
  formatDateAsDayWithSuffixShortMonth,
  formatTimeHoursMins,
  rotaDateIsToday,
} from "@/lib/date-functions";
import {
  getShiftsForUserId,
  filterShiftsForDayIndexRange,
} from "@/lib/rota/shift-functions";

export default {
  name: "RotaUserAgendaViewDay",
  components: {
    Placeholder,
    UserDisplayResolver,
    RotaActivityCard,
    RotaShiftWithCoverRequest,
  },
  props: {
    rota: {
      type: Object,
      default() {
        return {};
      },
    },
    rotaShiftsForUserOrNobody: {
      type: Array,
      default() {
        return [];
      },
    },
    rotaUsers: {
      type: Array,
      default() {
        return [];
      },
    },
    dayIndex: {
      type: Number,
      default() {
        return 0;
      },
    },
    activities: {
      type: [Array, Boolean],
      default() {
        return false;
      },
    },
    activitiesNotAssignedWithoutSupport: {
      type: Array,
      default() {
        return [];
      },
    },
    oncall: {
      type: [Array, Boolean],
      default() {
        return false;
      },
    },
    rotaAgencies: {
      type: Array,
      default() {
        return [];
      },
    },
    locationClients: {
      type: Array,
      default() {
        return [];
      },
    },
  },
  emits: ["rota-shift-self-assign"],
  setup(props) {
    const authStore = useAuthStore();

    const shiftsForDayIndex = computed(() => {
      return filterShiftsForDayIndexRange(
        props.rotaShiftsForUserOrNobody,
        props.rota.startDate,
        props.dayIndex,
        props.dayIndex,
      );
    });

    const activitiesForDayIndex = computed(() =>
      getActivitiesForRotaDay(
        props.activitiesNotAssignedWithoutSupport,
        props.rota.startDate,
        props.dayIndex,
      ),
    );

    const shiftsForUserId = computed(() => {
      return getShiftsForUserId(
        shiftsForDayIndex.value,
        authStore.isAgencyUser ? authStore.user.agencyId : authStore.user.id,
        undefined,
        undefined,
        undefined,
        ["current", "sick", "holiday", "old"],
      );
    });
    const shiftsNotForUserId = computed(() => {
      const shiftsForUser = shiftsForUserId.value;
      return shiftsForDayIndex.value.filter(
        (shift) => shiftsForUser.findIndex((s) => s.id === shift.id) === -1,
      );
    });
    const currentShiftsForUserId = computed(() => {
      return getShiftsForUserId(
        shiftsForDayIndex.value,
        authStore.isAgencyUser ? authStore.user.agencyId : authStore.user.id,
        undefined,
        undefined,
        undefined,
        ["current"],
      );
    });

    const dayIsToday = computed(() => {
      const d = addDays(
        setMilliseconds(props.rota.startDate, 0),
        props.dayIndex,
      );
      return isToday(d);
    });

    return {
      authStore,
      shiftsForDayIndex,
      activitiesForDayIndex,
      getDayClass: computed(() => {
        return {
          today: rotaDateIsToday(props.rota.startDate, props.dayIndex),
          "no-current": currentShiftsForUserId.value.length === 0,
        };
      }),
      getShiftAsignmentType: (shift) => {
        const match = findLast(shift.assignedTo, {
          userId: authStore.user.id,
        });
        return match ? match.type : "";
      },
      getOncallUsersForDayIndex: computed(() => {
        if (props.oncall === false) {
          return {};
        }
        const onCallPeople = filter(props.oncall, (oncall) =>
          isSameDay(oncall.date, addDays(props.rota.startDate, props.dayIndex)),
        );
        return onCallPeople.map((oncall) => {
          const user = find(props.rotaUsers, { userId: oncall.userId });
          return {
            ...user,
            ...oncall,
            phone: get(user, "phone", ""),
          };
        });
      }),
      dayIsCurrent: computed(() => {
        const d = addDays(
          setMilliseconds(props.rota.startDate, 0),
          props.dayIndex,
        );
        return isToday(d) || isFuture(d);
      }),
      dayIsToday,
      getDayAriaLabel: computed(() => {
        let result = `${formatDateAsDayWithSuffixShortMonth(props.rota.startDate, props.dayIndex)}${dayIsToday.value ? " (today)" : ""}`;

        if (currentShiftsForUserId.value.length > 0) {
          result +=
            " " +
            formatTimeHoursMins(currentShiftsForUserId.value[0].startDate) +
            " to " +
            formatTimeHoursMins(currentShiftsForUserId.value[0].endDate);
        } else {
          result += " No shifts";
        }

        return result;
      }),
      getShiftsForUserId,
      shiftsForUserId,
      currentShiftsForUserId,
      shiftsNotForUserId,
      formatDateAsDayWithSuffixShortMonth,
      formatTimeHoursMins,
      rotaDateIsToday,
    };
  },
};
</script>
